import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  limit,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Brand, Category, AttributeDefinition } from '@/types/schemas';

export type SetupItem = { id: string; name: string; categoryId?: string };

function tsToMs(v: unknown): number | undefined {
  if (!v) return undefined;
  if (typeof v === 'number') return v;
  if (typeof v === 'object' && v !== null && 'toMillis' in v) {
    return (v as { toMillis: () => number }).toMillis();
  }
  return undefined;
}

function mapCategory(id: string, data: Record<string, unknown>): Category {
  const name = data.name as string;
  return {
    id,
    name,
    codePrefix: (data.codePrefix as string) ?? deriveCodePrefix(name),
    attributeSchema: (data.attributeSchema as AttributeDefinition[]) ?? [],
    lowStockThresholdDefault: (data.lowStockThresholdDefault as number) ?? 5,
    hasExpiry: (data.hasExpiry as boolean) ?? false,
    isActive: (data.isActive as boolean) ?? true,
    archivedAt: data.archivedAt ? tsToMs(data.archivedAt) : null,
    archivedBy: (data.archivedBy as string) ?? null,
    createdAt: tsToMs(data.createdAt),
    updatedAt: tsToMs(data.updatedAt),
  };
}

// ----- Rich categories -----
export async function fetchCategories(): Promise<Category[]> {
  const snap = await getDocs(
    query(collection(db, 'inventory_categories'), orderBy('name')),
  );
  return snap.docs
    .map((d) => mapCategory(d.id, d.data() as Record<string, unknown>))
    .filter((c) => c.isActive !== false);
}

export async function fetchArchivedCategories(): Promise<Category[]> {
  const snap = await getDocs(
    query(collection(db, 'inventory_categories'), orderBy('name')),
  );
  return snap.docs
    .map((d) => mapCategory(d.id, d.data() as Record<string, unknown>))
    .filter((c) => c.isActive === false);
}

export async function fetchCategoryById(id: string): Promise<Category | null> {
  const snap = await getDoc(doc(db, 'inventory_categories', id));
  if (!snap.exists()) return null;
  return mapCategory(snap.id, snap.data() as Record<string, unknown>);
}

export async function isCodePrefixAvailable(codePrefix: string): Promise<boolean> {
  const snap = await getDocs(
    query(
      collection(db, 'inventory_categories'),
      where('codePrefix', '==', codePrefix.toUpperCase()),
      limit(1),
    ),
  );
  return snap.empty;
}

export async function createCategory(
  data: Omit<Category, 'id' | 'createdAt' | 'updatedAt' | 'archivedAt' | 'archivedBy'>,
): Promise<string> {
  const ref = await addDoc(collection(db, 'inventory_categories'), {
    ...data,
    codePrefix: data.codePrefix.toUpperCase(),
    isActive: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateCategory(
  id: string,
  data: Partial<Category>,
): Promise<void> {
  const rest = { ...data };
  delete rest.id;
  delete rest.createdAt;
  delete rest.updatedAt;
  await updateDoc(doc(db, 'inventory_categories', id), {
    ...rest,
    ...(rest.codePrefix ? { codePrefix: rest.codePrefix.toUpperCase() } : {}),
    updatedAt: serverTimestamp(),
  });
}

export async function archiveCategory(id: string, uid: string): Promise<void> {
  await updateDoc(doc(db, 'inventory_categories', id), {
    isActive: false,
    archivedAt: serverTimestamp(),
    archivedBy: uid,
    updatedAt: serverTimestamp(),
  });
}

export async function restoreCategory(id: string): Promise<void> {
  await updateDoc(doc(db, 'inventory_categories', id), {
    isActive: true,
    archivedAt: null,
    archivedBy: null,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteCategory(id: string): Promise<void> {
  await deleteDoc(doc(db, 'inventory_categories', id));
}

/** Legacy name lookup for dropdowns */
export function categoryDisplayName(c: Category): string {
  return c.name;
}

// ----- Brands / sub-brands / units (setup lists) -----
export async function fetchSubBrands(): Promise<SetupItem[]> {
  const snap = await getDocs(
    query(collection(db, 'inventory_sub_brands'), orderBy('name')),
  );
  return snap.docs.map((d) => ({ id: d.id, name: d.data().name as string }));
}

export async function addSubBrand(name: string): Promise<string> {
  const ref = await addDoc(collection(db, 'inventory_sub_brands'), {
    name: name.trim(),
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function deleteSubBrand(id: string): Promise<void> {
  await deleteDoc(doc(db, 'inventory_sub_brands', id));
}

function mapBrand(id: string, data: Record<string, unknown>): Brand {
  return {
    id,
    name: data.name as string,
    categoryId: (data.categoryId as string) ?? '',
    createdAt: tsToMs(data.createdAt),
  };
}

/** Fetch brands, optionally scoped to a category */
export async function fetchBrands(categoryId?: string): Promise<Brand[]> {
  const snap = await getDocs(
    query(collection(db, 'inventory_brands'), orderBy('name')),
  );
  const all = snap.docs.map((d) => mapBrand(d.id, d.data() as Record<string, unknown>));
  if (!categoryId) return all;
  return all.filter((b) => b.categoryId === categoryId);
}

export async function addBrand(name: string, categoryId: string): Promise<string> {
  const ref = await addDoc(collection(db, 'inventory_brands'), {
    name: name.trim(),
    categoryId,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function findOrCreateBrand(
  name: string,
  categoryId: string,
): Promise<{ id: string; name: string }> {
  const trimmed = name.trim();
  const brands = await fetchBrands(categoryId);
  const existing = brands.find((b) => b.name.toLowerCase() === trimmed.toLowerCase());
  if (existing) return { id: existing.id, name: existing.name };
  const id = await addBrand(trimmed, categoryId);
  return { id, name: trimmed };
}

export async function deleteBrand(id: string): Promise<void> {
  await deleteDoc(doc(db, 'inventory_brands', id));
}

export async function fetchUnits(): Promise<SetupItem[]> {
  const snap = await getDocs(
    query(collection(db, 'inventory_units'), orderBy('name')),
  );
  return snap.docs.map((d) => ({ id: d.id, name: d.data().name as string }));
}

export async function addUnit(name: string): Promise<string> {
  const ref = await addDoc(collection(db, 'inventory_units'), {
    name: name.trim(),
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function deleteUnit(id: string): Promise<void> {
  await deleteDoc(doc(db, 'inventory_units', id));
}

/** Derive SKU prefix from category name when migrating legacy categories */
export function deriveCodePrefix(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const initials = words
    .map((w) => w[0])
    .join('')
    .toUpperCase();
  const cleaned = name.replace(/[^A-Za-z]/g, '').toUpperCase();
  const base = initials.length >= 2 ? initials : cleaned;
  return (base || 'CAT').slice(0, 5);
}
